// apps/client/src/game/testHooks.ts — `window.__wt`, attached when import.meta.env.DEV || VITE_E2E === '1' (§8.11).
// M2-T12 adds perf, setQuality, openScreen, devClock, devGrant and townNow together with their implementation.
import { tuning, type CatalogId } from '@whiskerton/content';
import type { ActionReq, ActionType, ChatLine, DayKey, ItemView, ResidentId, SpaceId, TownId, Vec2 } from '@whiskerton/shared';
import { api } from '../api/endpoints';
import { lastPerfSample, type PerfSample } from '../engine/perf';
import type { ActResult } from '../net/actions';
import type { Phase } from '../state/slices/core';
import { useGame } from '../state/store';
import type { Game } from './game';
import { testFinderModules } from './testFinders.gen';

export interface TestHooks {
  ready(): Promise<void>;                                    // in town, shaders compiled, the world drawn and the socket live
  state(): { phase: Phase; townId: TownId | null; space: SpaceId; pos: Vec2 | null; bells: number | null;
             items: ItemView[]; others: { r: ResidentId; space: SpaceId; pos: Vec2 | null }[]; chat: ChatLine[] };
  walkTo(x: number, y: number): Promise<boolean>;            // resolves on arrival (true), or false: no path, or the walk was cancelled
  act<T extends ActionType>(type: T, req: ActionReq<T>): Promise<ActResult<T>>;
  find(kind: string, near?: Vec2): { id: string; x: number; y: number } | null;   // first finder with that kind; unknown kind → null
  kinds(): string[];                                         // every registered finder kind
  press(button: 'A' | 'B'): void;
  icon(id: CatalogId): Promise<string>;                      // M1 gap fix: the item icon's data URL (AssetService.icon), for pixel checks
  // rev 5 (M2-T12):
  perf(): { sample: PerfSample | null; budget: { maxDrawCalls: number; maxDpr: number; minFps: number; targetFps: number } };
  setQuality(q: 'auto' | 'low' | 'high'): void;
  openScreen(id: string, params?: unknown): boolean;         // false when the screen isn't registered
  devClock(at: string): Promise<void>;                       // town-local 'YYYY-MM-DDTHH:mm'; then devRunJobs, re-enter the town (fresh snapshot, clock resync), ready()
  devGrant(req: { bells?: number; items?: CatalogId[]; museum?: 'full' }): Promise<void>;   // museum: 'full' donates every fossil and fish
  townNow(): { dayKey: DayKey | null; minuteOfDay: number };   // rev 5 item 22: = WorldSlice.clock
}
/** Registry game/testFinders/<feature>.ts: `export const finders: readonly TestFinder[]`. */
export interface TestFinder { kind: string; find(game: Game, near: Vec2 | null): { id: string; x: number; y: number } | null }
declare global { interface Window { __wt?: TestHooks } }

/** The screen registry (`hasScreen`), loaded when the hooks are first installed, never at module load: a static import would close
 *  the cycle screen → game → commands/core → testHooks → ui/router → every screen, and the router would build its registry from a
 *  screen module that is still loading. `ready()` awaits this import, so `openScreen` after `ready()` never answers false early. The
 *  import sits behind the build-time flags (not IS_DEV/IS_E2E), so a production bundle drops it at build time and carries no import(). */
let router: typeof import('../ui/router') | null = null;
let routerLoad: Promise<void> | null = null;
const loadRouter = (): void => {
  if (import.meta.env.DEV || import.meta.env.VITE_E2E === '1') routerLoad ??= import('../ui/router').then((m) => { router = m; });
};

const allFinders = (): readonly TestFinder[] => (testFinderModules as readonly { finders: readonly TestFinder[] }[]).flatMap((m) => m.finders);

export function installTestHooks(game: Game): void {
  if (typeof window === 'undefined') return;
  loadRouter();
  const hooks: TestHooks = {
    // The screen registry is loaded too, so openScreen is deterministic; the socket is live, so the next act is never 'offline'.
    ready: async () => { await routerLoad; await game.whenReady(); },
    state() {
      const s = useGame.getState(), me = s.you?.resident ?? null, mine = game.you();
      const remotes = new Map(game.remotePositions().map((p) => [p.id, { x: p.x, y: p.y }]));
      return {
        phase: s.phase, townId: s.townId, space: s.space, pos: mine?.pos ?? null, bells: s.you?.bells ?? null, items: s.you?.items ?? [],
        others: Object.values(s.presence).filter((p) => p.r !== me).map((p) => ({ r: p.r, space: p.space, pos: remotes.get(p.r) ?? null })),
        chat: (s as { chatLines?: ChatLine[] }).chatLines ?? [],
      };
    },
    walkTo: (x, y) => new Promise((resolve) => {
      if (!game.walkTo({ x: Math.floor(x), y: Math.floor(y) }, () => resolve(true), () => resolve(false))) resolve(false);
    }),
    act: (type, req) => game.cmd.act(type, req),
    find(kind, near) { return allFinders().find((f) => f.kind === kind)?.find(game, near ?? null) ?? null; },
    kinds: () => allFinders().map((f) => f.kind),
    press: (button) => game.cmd.press(button),
    icon: (id) => game.assets.icon(id),
    perf: () => ({ sample: lastPerfSample(),
      budget: { maxDrawCalls: tuning.render.maxDrawCalls, maxDpr: tuning.render.maxDpr, minFps: tuning.render.minFps, targetFps: tuning.render.targetFps } }),
    townNow: () => { const c = useGame.getState().clock; return { dayKey: c.dayKey, minuteOfDay: c.minuteOfDay }; },   // rev 5 item 22
    setQuality: (q) => game.cmd.setQuality(q),            // stores the setting and applies the resolved level, as the settings screen does
    openScreen: (id, params) => { if (!router?.hasScreen(id)) return false; useGame.getState().openScreen(id, params as never); return true; },
    devClock: async (at) => {
      // rev 5 item 17. A manual town runs no jobs on its own, and the client re-syncs its clock offset only periodically. So after
      // moving the clock: run the jobs (the 05:00 day change sends the new yields), then re-enter for a fresh snapshot (today's
      // dig spots, villagers, pauses) and a fresh offset (RoutineCache places villagers by the town clock, not real time).
      const id = useGame.getState().townId;
      if (!id) return;
      await api.devClock(id, { at });
      await api.devRunJobs(id);
      game.cmd.leaveTown();
      await game.cmd.enterTown(id);
      await game.socket?.clock.sync();
      await hooks.ready();
    },
    devGrant: async (req) => { const id = useGame.getState().townId; if (id) await api.devGrant(id, req); },
  };
  window.__wt = hooks;
}
